import { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useGetAssociatePosts } from '@vs-online-only-app/state';
import { Skeleton, Stack } from '@mui/material';
import PostCard, { CARD_HEIGHT } from './PostCard';
import PostsPlaceholder from './PostsPlaceholder';

export const PostSkeleton = styled(Skeleton)`
  height: ${CARD_HEIGHT}px;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
`;

type Props = {
  paginate?: boolean;
};

const PostsList: FC<Props> = ({ paginate }) => {
  const { data, fetchNextPage, isLoading, isFetching, hasNextPage } =
    useGetAssociatePosts();

  // If paginate is true, flatten all pages
  // Otherwise, only show the first page
  const posts = useMemo(() => {
    if (!data) return [];
    if (paginate) return data.pages.flatMap(({ posts }) => posts);
    return data.pages[0].posts;
  }, [data, paginate]);

  const totalCount = data?.pages[0].totalCount || 0;

  const callbackRef = useCallback(
    (container: HTMLDivElement) => {
      const containerParent = container?.parentElement;
      if (!paginate || !containerParent) return;

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = containerParent;
        const scrollBottom = scrollHeight - scrollTop - clientHeight;

        // Use a threshold of 0.5 to since the scroll event might be unreliable
        if (scrollBottom <= 0.5 && !isFetching && hasNextPage) {
          fetchNextPage();
        }
      };

      containerParent.addEventListener('scroll', handleScroll);
    },
    [fetchNextPage, hasNextPage, isFetching, paginate],
  );

  if (posts.length === 0 && !isLoading) {
    return <PostsPlaceholder />;
  }

  return (
    <Stack gap={2} overflow="auto" ref={callbackRef} paddingBottom={3}>
      {isLoading
        ? Array.from({ length: 3 }).map((_, index) => (
            <PostSkeleton key={index} variant="rectangular" />
          ))
        : posts.map((post) => <PostCard key={post._id} {...post} />)}
      {isFetching && posts.length < totalCount && (
        <PostSkeleton variant="rectangular" />
      )}
    </Stack>
  );
};

export default PostsList;
